import {
  DirectoryGroupingConsts, ItemTypeConsts, SoftwareAppDesiredStatusConsts, StoreModuleMap,
} from 'client/util/Constants';
import { cloneDeep } from 'lodash';
import SoftwareAppsApi from 'client/api/v2/SoftwareAppsApi';
import Vue from 'vue';
import createModel, { getStoreModuleType } from 'client/store/modules/models/createModel';

const { groupTypes, memberTypes } = DirectoryGroupingConsts;

export const getDefaultItem = () => ({
  id: '',
  displayName: '',
  settings: [{
    allowUpdateDelay: false,
    appleVpp: {},
    assetKind: '',
    assetSha256Size: 0,
    assetSha256Strings: [],
    autoUpdate: false,
    description: '',
    desiredState: SoftwareAppDesiredStatusConsts.INSTALL.value,
    location: '',
    locationObjectId: '',
    packageId: '',
    packageKind: '',
    packageManager: 'CHOCOLATEY',
    packageVersion: '',
  }],
  softwareAppId: '',
});

export const createSoftwareAppsModel = () => {
  const model = createModel(getDefaultItem, 'id', SoftwareAppsApi);

  return {
    namespaced: true,

    state: {
      ...model.state,
    },

    actions: {
      ...model.actions,

      bindSoftwareAppToSystems(context) {
        return context.dispatch('bindAssociations', {
          parentType: memberTypes.softwareApp,
          targetType: memberTypes.system,
        });
      },

      bindSoftwareAppToSystemGroups(context) {
        return context.dispatch('bindAssociations', {
          parentType: memberTypes.softwareApp,
          targetType: groupTypes.systemGroup,
        });
      },

      reclaimLicenses({ commit, state }) {
        const { id } = state.item;
        const newItem = cloneDeep(state.item);

        return SoftwareAppsApi.reclaimLicenses(id)
          .then((licenses) => {
            newItem.settings[0].appleVpp.assignedLicenses = licenses.assignedLicenses;
            newItem.settings[0].appleVpp.availableLicenses = licenses.availableLicenses;
            newItem.settings[0].appleVpp.totalLicenses = licenses.totalLicenses;
            newItem.settings[0].appleVpp.reclaimedLicenses = licenses.reclaimedLicenses;

            commit('SET_ITEM', newItem);
            commit('SET_PREVIOUS_ITEM', newItem);

            return licenses;
          });
      },

      saveItem(context) {
        return model
          .actions.saveItem(context)
          .then((item) => {
            const action = `${StoreModuleMap[ItemTypeConsts.softwareApps]}/refreshCollection`;
            context.dispatch(action, { item }, { root: true });
            return item;
          });
      },

      validateUrl({ commit, state }, url) {
        const newItem = cloneDeep(state.item);

        return SoftwareAppsApi.validateCustomAppUrl(url)
          .then((data) => {
            newItem.displayName = data.title;
            newItem.settings[0].assetKind = data.assetKind;
            newItem.settings[0].assetSha256Size = data.assetSha256Size;
            newItem.settings[0].assetSha256Strings = data.assetSha256Strings;
            newItem.settings[0].location = data.assetUrl;
            newItem.settings[0].packageKind = data.packageKind;
            newItem.settings[0].packageId = data.bundleIdentifier;
            newItem.settings[0].packageVersion = data.bundleVersion;
            newItem.settings[0].packageManager = 'APPLE_CUSTOM';

            commit('SET_ITEM', newItem);
            commit('SET_PREVIOUS_ITEM', newItem);
          });
      },

      setItemSettings({ commit }, { key, value }) {
        commit('SET_ITEM_SETTINGS', { [key]: value });
      },

      setItemGoogleAndroid({ commit }, { key, value }) {
        commit('SET_ITEM_ANDROID', { [key]: value });
      },
    },

    getters: {
      ...model.getters,

      displayName(state = { item: {} }) {
        const { displayName } = state.item;
        return displayName || '';
      },

      haveAssociationsChanged(state, getters) {
        return model.getters.haveAssociationsChanged(state, getters)(
          [memberTypes.system, groupTypes.systemGroup],
        );
      },

      getTotalAssociations(state, getters, rootState, rootGetters) {
        const getTargetsTotalCount = (targetType) => {
          const type = getStoreModuleType(targetType);
          const module = StoreModuleMap[type];
          return rootGetters[`${module}/getBoundItemsTotalCount`];
        };

        const boundDeviceTotalCount = getTargetsTotalCount(memberTypes.system);
        const boundDeviceGroupTotalCount = getTargetsTotalCount(groupTypes.systemGroup);

        return { boundDeviceTotalCount, boundDeviceGroupTotalCount };
      },

      getModifiedAssociations(state, getters, rootState, rootGetters) {
        // get system associations
        const systemsToBind = model.getters
          .getTargetsToBind(state, getters, rootState, rootGetters)(memberTypes.system);
        const systemsToUnbind = model.getters
          .getTargetsToUnbind(state, getters, rootState, rootGetters)(memberTypes.system);

        // get system group associations
        const systemGroupsToBind = model.getters
          .getTargetsToBind(state, getters, rootState, rootGetters)(groupTypes.systemGroup);
        const systemGroupsToUnbind = model.getters
          .getTargetsToUnbind(state, getters, rootState, rootGetters)(groupTypes.systemGroup);

        return {
          systemsToBind, systemsToUnbind, systemGroupsToBind, systemGroupsToUnbind,
        };
      },

      getInstallStatus(state = { item: {} }) {
        const { desiredState, autoUpdate } = state.item.settings[0];
        if (autoUpdate) return SoftwareAppDesiredStatusConsts.autoUpdate.value;
        return SoftwareAppDesiredStatusConsts[desiredState].value;
      },
    },

    mutations: {
      ...model.mutations,

      SET_ITEM_SETTINGS(state = { item: {} }, item = {}) {
        Object.keys(item).forEach(key => Vue.set(state.item.settings[0], key, item[key]));
      },

      SET_ITEM_ANDROID(state = { item: {} }, item = {}) {
        Object.keys(item).forEach(
          key => Vue.set(state.item.settings[0].googleAndroid, key, item[key]),
        );
      },
    },
  };
};

export default createSoftwareAppsModel();
